import { useCallback, useState, cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import Text from '@/elements/Text'
import Modal from '@/components/Modal'
import { deleteOrganizationMember } from '@/api/organizationMember'
import { deleteOrganizationIndividual } from '@/api/organizationIndividual'
import toast from '@/utils/toast'

interface UseDeactivateUserProps {
  userType: 'individual' | 'member'
  userId: string
  refetch: () => void
  redirect?: boolean
}

interface DeactivateConfig {
  title: string
  description: string
  apiCall: () => Promise<{ success: boolean }>
  successMessage: string
  errorMessage: string
  redirectPath?: string
}

const useDeactivateUser = ({
  userType,
  userId,
  refetch,
  redirect,
}: UseDeactivateUserProps) => {
  const [showDeactivateModal, setShowDeactivateModal] = useState(false)
  const navigate = useNavigate()

  const getDeactivateConfig = useCallback((): DeactivateConfig | null => {
    switch (userType) {
      case 'member':
        return {
          title: 'Deactivate Member',
          description: 'Are you sure you want to deactivate this member? This action cannot be undone.',
          apiCall: () => deleteOrganizationMember(userId),
          successMessage: 'Member removed successfully.',
          errorMessage: 'Failed to remove member.',
          redirectPath: '/organization/members'
        }
      case 'individual':
        return {
          title: 'Deactivate Individual',
          description: 'Are you sure you want to deactivate this individual? This will remove them from Watchdog monitoring.',
          apiCall: () => deleteOrganizationIndividual(userId),
          successMessage: 'User deactivated successfully.',
          errorMessage: 'Failed to deactivate user.',
          redirectPath: '/organization/individuals'
        }
      default:
        return null
    }
  }, [userType, userId])

  const handleDeactivateUser = useCallback(async () => {
    const config = getDeactivateConfig()
    if (!config) return
    try {
      const res = await config.apiCall()

      if (res.success) {
        toast.success(config.successMessage)
        refetch()
        setShowDeactivateModal(false)

        if (redirect && config.redirectPath) {
          navigate(config.redirectPath)
        }
      } else {
        toast.error(config.errorMessage)
      }
    } catch {
      toast.error(config.errorMessage)
    }
  }, [getDeactivateConfig, refetch, redirect, navigate])

  const DeactivateUserModal = ({ children }: { children?: ReactNode }) => {
    const config = getDeactivateConfig()

    const renderTrigger = () => {
      if (children && isValidElement(children)) {
        const element = children as ReactElement<{ onClick?: (e: React.MouseEvent) => void }>
        return cloneElement(element, {
          onClick: (e: React.MouseEvent) => {
            e?.preventDefault?.()
            setShowDeactivateModal(true)
            if (element.props?.onClick) {
              element.props.onClick(e)
            }
          }
        })
      }
      return null
    }

    return (
      <>
        {renderTrigger()}
        <Modal
          isOpen={showDeactivateModal}
          title={config?.title}
          onSave={handleDeactivateUser}
          onCancel={() => setShowDeactivateModal(false)}
          cancelLabel='No'
          saveLabel='Yes'
        >
          <Text color='black' style={{ hyphens: 'none' }}>
            {config?.description}
          </Text>
        </Modal>
      </>
    )
  }

  return {
    showDeactivateModal,
    setShowDeactivateModal,
    handleDeactivateUser,
    DeactivateUserModal
  }
}

export default useDeactivateUser
